import { useCallback, useEffect, useState } from 'react'
import { t } from 'ttag'
import { useDispatch, useSelector } from 'react-redux'

import ComparisonFooter from '@main/components/ComparisonFooter'
import { MilitaryPersonIcon, OccupationsIcon } from '@h2/components/icons'
import { Button } from '@h2/components/buttons/button'
import { Flex } from '@h2/components/layout/flex'
import { Grid } from '@h2/components/containers/grid'
import { setForComparison } from '@main/actions/occupationProfile'
import { Link } from '@h2/common/components/link'

const SimilarOccupations = ({
  currentOccupation,
  showMilitary,
  relatedOnet,
  relatedMilitary,
  occupationMeta
}) => {
  const [selectedTab, setSelectedTab] = useState(1)
  const [tableColumns, setTableColumns] = useState([])
  const [data, setData] = useState([])
  const handleSelect = selectedKey => {
    setSelectedTab(selectedKey)
  }
  const { jobZoneValues } = useSelector(state => state.jobZones)
  const dispatch = useDispatch()
  const handleRowSelect = selectedValue => {
    const rowWithCurrent =
      selectedValue.length > 0 ? [...selectedValue, currentOccupation] : []

    dispatch(setForComparison(rowWithCurrent.map(occ => occ.onetSoc)))
  }
  const generateColumns = useCallback(() => {
    const inBudget = (occupationMeta && occupationMeta.inBudget) || {}
    const flags = (occupationMeta && occupationMeta.flags) || {}

    switch (selectedTab) {
      case 2:
        return [
          {
            id: 'title',
            sortable: false,
            width: '60%',
            label: t`Occupation`,
            render({ title, url }) {
              return <Link href={url}>{title}</Link>
            }
          },
          {
            id: 'personnelCategory',
            sortable: false,
            label: t`Category`,
            width: '20%',
            render({ personnelCategory }) {
              return personnelCategory?.name
            }
          },
          {
            id: 'branchOfService',
            sortable: false,
            label: t`Branch`,
            width: '20%',
            render({ branchOfService }) {
              return branchOfService?.name
            }
          }
        ]
      default:
        return [
          {
            id: 'title',
            label: t`Title`,
            width: '80%',
            sortable: false,
            render({ onetSoc, url, title }) {
              return (
                <>
                  <Link href={url}>{title}</Link>
                  {inBudget[onetSoc] && (
                    <span
                      className="badge listing-badge budget defined"
                      title={t`This occupation would satisfy the salary requirements from your budget.`}>
                      <span>{t`In Budget`}</span>
                    </span>
                  )}
                  {(flags[onetSoc] || []).map((flag, idx) => (
                    <span
                      key={`flag-${idx}`}
                      className={`badge listing-badge ${flag.styleClass} defined`}
                      title={flag.description}>
                      <span>{flag.name}</span>
                    </span>
                  ))}
                </>
              )
            }
          },
          {
            id: 'jobZone',
            label: t`JobZone`,
            sortable: false,
            render({ jobZone }) {
              const jz = jobZoneValues[jobZone] || {}

              return (
                <div className="job-zone" title={jz.description}>
                  {jobZone}
                </div>
              )
            }
          }
        ]
    }
  }, [jobZoneValues, occupationMeta, selectedTab])

  useEffect(() => {
    setData(selectedTab === 2 ? relatedMilitary : relatedOnet)
    setTableColumns(generateColumns())
    dispatch(setForComparison([]))
  }, [dispatch, selectedTab, generateColumns, relatedMilitary, relatedOnet])

  return (
    <>
      {showMilitary && (
        <Flex gap="5px" margin="5px 0 15px 0">
          <Button
            display={selectedTab === 1 ? 'blue' : 'monochrome'}
            variant={selectedTab === 1 ? null : 'outlined'}
            onClick={() => handleSelect(1)}
            size="medium"
            type="button"
            align="right"
            icon={OccupationsIcon}>
            {t`In the Workforce`}
          </Button>
          <Button
            display={selectedTab === 2 ? 'blue' : 'monochrome'}
            variant={selectedTab === 2 ? null : 'outlined'}
            onClick={() => handleSelect(2)}
            size="medium"
            type="button"
            align="right"
            icon={MilitaryPersonIcon}>
            {t`In the Military`}
          </Button>
        </Flex>
      )}
      {data.length === 0 ? null : (
        <Grid
          columns={tableColumns}
          data={data}
          onSelectRow={handleRowSelect}
          options={{ disableFilters: true, fullWidth: true }}
          rowIdKey={selectedTab === 2 ? 'moc' : 'onetSoc'}
          isPagerVisible={false}
          isRowSelectable={selectedTab === 1}
        />
      )}
      <ComparisonFooter />
    </>
  )
}

export { SimilarOccupations }
